// One-way "Set as Shared Scanner" confirmation modal. Lives in the user
// dropdown (top-right). After confirm, the device's localStorage flips to
// 'shared' and the next scanner session requires badge+PIN. Reverting requires
// clearing the browser's site data — intentional so an accidental click can't
// silently degrade audit integrity.

import React from 'react';
import { instance } from '../instance';

interface Props {
  onCancel: () => void;
  onConfirm: () => void;
}

const SetSharedDeviceModal: React.FC<Props> = ({ onCancel, onConfirm }) => (
  <div
    role="dialog"
    aria-modal="true"
    aria-labelledby="set-shared-modal-title"
    className="scrim z-[100] flex items-center justify-center px-4"
    // Backdrop click does NOT dismiss — this is a one-way destructive action.
  >
    <div className="bg-[#242424] rounded-lg shadow-xl max-w-md w-full p-6 border border-amber-700/60">
      <h3 id="set-shared-modal-title" className="text-lg font-semibold text-amber-300 mb-3">
        Set as Shared Scanner?
      </h3>
      <div className="text-sm text-on-surface space-y-2 mb-5">
        <p>
          From now on, every scanner session on this browser will require an operator to
          badge in with their ID and 4-digit PIN.
        </p>
        <p className="text-amber-200/80 font-medium">
          This is one-way. To switch back, clear this browser's site data.
        </p>
        <p className="text-xs text-on-surface-variant">
          Audit identity for all scanner actions becomes the <em>badged operator</em>, not the
          {' '}{instance.displayName} account this browser is logged in as.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm font-medium rounded bg-gray-700/60 text-on-surface hover:bg-gray-750 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-outline"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className="px-3 py-1.5 text-sm font-medium rounded bg-amber-600/20 text-amber-300 ring-1 ring-inset ring-amber-700/60 hover:bg-amber-600/30 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        >
          Yes, make shared
        </button>
      </div>
    </div>
  </div>
);

export default SetSharedDeviceModal;
